import React, {
  FC,
  InputHTMLAttributes,
  ReactNode,
  TextareaHTMLAttributes,
  useCallback,
  WheelEvent
} from 'react';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons/faExclamationCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useController } from 'react-hook-form';
import classNames from 'classnames';

import styles from './fieldStyles.scss';

type InputProps = {
  type: 'text' | 'number' | 'date';
  endAdornment?: string;
  inputProps?: Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'name' | 'onBlur' | 'onChange' | 'size' | 'type' | 'value'
  >;
};

type TextAreaProps = {
  type: 'textarea';
  textAreaProps?: Omit<
    TextareaHTMLAttributes<HTMLTextAreaElement>,
    'name' | 'onBlur' | 'onChange' | 'value'
  >;
};

type SharedProps = {
  disabled?: boolean;
  generateHelperElement?: (value: string) => ReactNode;
  label?: string;
  name: string;
  onChangeCallback?: (value: string) => void;
  size?: 'small' | 'medium' | 'large';
};

const InputField: FC<SharedProps & (InputProps | TextAreaProps)> = props => {
  const {
    disabled,
    generateHelperElement,
    label,
    name,
    onChangeCallback,
    size = 'medium',
    type
  } = props;

  const {
    field: { onChange, value, ...field },
    fieldState: { error },
    formState: { isSubmitting }
  } = useController({ name });

  const isDisabled = disabled || isSubmitting;
  const hasEndAdornment = type !== 'textarea' && Boolean(props.endAdornment);

  const handleOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange(event.target.value);
      if (onChangeCallback) onChangeCallback(event.target.value);
    },
    [onChange, onChangeCallback]
  );

  const handleNumberInputOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const output = parseFloat(e.target.value);
      const value = isNaN(output) ? null : output;

      onChange(value);
      if (onChangeCallback) onChangeCallback(value?.toString() || '');
    },
    [onChange, onChangeCallback]
  );

  const handleNumberInputOnWheel = useCallback((e: WheelEvent<HTMLInputElement>) => {
    if (e.target instanceof HTMLInputElement) e.target.blur();
  }, []);

  return (
    <div
      className={classNames(styles.field, {
        [styles.small]: size === 'small',
        [styles.medium]: size === 'medium',
        [styles.large]: size === 'large',
        [styles.hasError]: Boolean(error),
        [styles.hasEndAdornment]: hasEndAdornment
      })}
    >
      {label ? (
        <label data-title htmlFor={name}>
          {label}
        </label>
      ) : null}
      <div className={styles.inputWrapper}>
        {type === 'textarea' ? (
          <textarea
            {...props.textAreaProps}
            {...field}
            className={styles.inputField}
            disabled={isDisabled}
            id={name}
            onChange={handleOnChange}
            value={value || ''}
          />
        ) : (
          <input
            {...props.inputProps}
            {...field}
            className={styles.inputField}
            disabled={isDisabled}
            id={name}
            onWheel={type === 'number' ? handleNumberInputOnWheel : undefined}
            onChange={type === 'number' ? handleNumberInputOnChange : handleOnChange}
            type={type}
            value={value || ''}
          />
        )}
        {hasEndAdornment ? (
          <label
            className={classNames(styles.endAdornment, { [styles.disabledAdornment]: isDisabled })}
            htmlFor={name}
          >
            {props.endAdornment}
          </label>
        ) : null}
      </div>
      {error?.message ? (
        <label data-error htmlFor={name}>
          <FontAwesomeIcon icon={faExclamationCircle} />
          {error.message}
        </label>
      ) : null}
      {generateHelperElement ? generateHelperElement(value || '') : null}
    </div>
  );
};

export default InputField;
